import { capitalize } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import RichTextEditor from '../../components/rich-text-editor/rich-text-editor';
import { ROUTE_PATH } from '../../routes/paths';
import {
  emptyProviderTypeConfig,
  ProviderTypeConfig,
  useCreateExternalProviderTypeConfig,
  useExternalProviderPlans,
  useExternalProviderTypeConfigs,
  useUpdateExternalProviderTypeConfig,
} from '../../hooks/useExternalProviderPlans';
import MeaganConfirmModal from './meagan-confirm-modal';

const parseTags = (raw: string): string[] =>
  raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
const providers = [
  { name: 'DialCare', value: 'dialcare' },
  { name: 'MeMD', value: 'me_md' },
  { name: 'Recuro', value: 'recuro' },
  { name: 'Opyn', value: 'opyn' },
  { name: 'Revive', value: 'revive' },
];

const allProviderTypes = ['counseling', 'primary', 'urgent', 'crisis', 'crisis_lite', 'rx', 'pediatric'];

const providerTypeLabels: Record<string, string> = {
  counseling: 'Counseling',
  primary: 'Primary Care',
  urgent: 'Urgent Care',
  crisis: 'Crisis',
  crisis_lite: 'Crisis Lite',
  rx: 'Prescription Benefit',
  pediatric: 'Pediatric Behavioral Health',
};

const providerLabels: Record<string, string> = Object.fromEntries(providers.map((p) => [p.value, p.name]));

const comboKey = (provider: string, providerType: string) => `${provider}::${providerType}`;

const PlusCareContentPage: React.FC = () => {
  const history = useHistory();
  const { data: plans, isLoading: plansLoading } = useExternalProviderPlans();
  const { data: configs, isLoading: configsLoading } = useExternalProviderTypeConfigs();
  const { createExternalProviderTypeConfig } = useCreateExternalProviderTypeConfig();
  const { updateExternalProviderTypeConfig } = useUpdateExternalProviderTypeConfig();

  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [configsByCombo, setConfigsByCombo] = useState<Record<string, ProviderTypeConfig>>({});
  const [dirtyCombos, setDirtyCombos] = useState<Set<string>>(new Set());
  const [expandedCombos, setExpandedCombos] = useState<Set<string>>(new Set());
  const [showMeaganModal, setShowMeaganModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize configs map keyed by (provider, provider_type), defaulting to empty entries.
  useEffect(() => {
    if (!configs) return;
    const next: Record<string, ProviderTypeConfig> = {};
    const expand = new Set<string>();
    for (const provider of providers) {
      for (const providerType of allProviderTypes) {
        const existing = configs.find((c) => c.provider === provider.value && c.provider_type === providerType);
        const key = comboKey(provider.value, providerType);
        if (existing) {
          next[key] = { ...existing };
          expand.add(key);
        } else {
          next[key] = emptyProviderTypeConfig(provider.value, providerType);
        }
      }
    }
    setConfigsByCombo(next);
    setExpandedCombos(expand);
    setDirtyCombos(new Set());
  }, [configs]);

  const usageCountsByCombo = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    if (!plans) return counts;
    for (const plan of plans) {
      for (const providerType of plan.provider_types) {
        const key = comboKey(plan.provider, providerType);
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [plans]);

  const updateConfig = (key: string, patch: Partial<ProviderTypeConfig>) => {
    setConfigsByCombo((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    setDirtyCombos((prev) => new Set(prev).add(key));
  };

  const toggleExpanded = (key: string) => {
    setExpandedCombos((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSave = () => {
    if (dirtyCombos.size === 0) return;
    setShowMeaganModal(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    const remaining = new Set(dirtyCombos);
    let savedCount = 0;
    try {
      for (const key of Array.from(remaining)) {
        const cfg = configsByCombo[key];
        if (cfg.id) {
          await updateExternalProviderTypeConfig.mutateAsync({ configId: cfg.id, config: cfg });
        } else {
          await createExternalProviderTypeConfig.mutateAsync(cfg);
        }
        remaining.delete(key);
        savedCount += 1;
      }
      setShowMeaganModal(false);
      setDirtyCombos(new Set());
      toast.success(`Saved ${savedCount} content change${savedCount === 1 ? '' : 's'}`);
    } catch {
      setDirtyCombos(remaining);
      toast.error(
        savedCount > 0
          ? `Saved ${savedCount}, but ${remaining.size} change${remaining.size === 1 ? '' : 's'} failed. Retry to finish.`
          : 'Failed to save content changes. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const isLoading = plansLoading || configsLoading;
  const filteredProviders = providerFilter === 'all' ? providers : providers.filter((p) => p.value === providerFilter);

  return (
    <div>
      <Header heading={'+Care · Manage Content'} />

      <div className="flex h-12 items-center justify-between border-b border-gray-200 bg-white px-7">
        <button
          type="button"
          onClick={() => history.push(ROUTE_PATH.PLUS_CARE)}
          className="text-blue-primary flex items-center gap-1.5 text-sm font-medium hover:underline"
        >
          <span className="text-base leading-none">‹</span>
          Back to +Care
        </button>
        <button
          type="button"
          onClick={handleSave}
          className={
            'rounded-sm px-5 py-1.5 text-sm font-medium transition-colors ' +
            (dirtyCombos.size > 0
              ? 'bg-blue-primary text-white hover:opacity-90'
              : 'cursor-not-allowed border border-gray-300 bg-white text-gray-400')
          }
          disabled={dirtyCombos.size === 0 || saving}
          data-testid="plus-care-content-save-button"
        >
          {saving ? 'Saving…' : `Save ${dirtyCombos.size > 0 ? `(${dirtyCombos.size})` : ''}`.trim()}
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-7 py-3">
        <span className="text-gray-dark mr-2 text-xs font-medium">Filter by provider:</span>
        <FilterChip label="All" active={providerFilter === 'all'} onClick={() => setProviderFilter('all')} />
        {providers.map((p) => (
          <FilterChip
            key={p.value}
            label={p.name}
            active={providerFilter === p.value}
            onClick={() => setProviderFilter(p.value)}
          />
        ))}
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="max-window-height overflow-y-auto px-7 py-6">
          {filteredProviders.map((provider) => (
            <div key={provider.value} className="mb-8">
              <h2 className="blue-primary mb-3 text-lg font-semibold">{provider.name}</h2>
              <div className="grid grid-cols-2 gap-4">
                {allProviderTypes.map((providerType) => {
                  const key = comboKey(provider.value, providerType);
                  const cfg = configsByCombo[key];
                  if (!cfg) return null;
                  return (
                    <ComboCard
                      key={key}
                      providerLabel={provider.name}
                      providerType={providerType}
                      typeLabel={providerTypeLabels[providerType] || capitalize(providerType)}
                      usageCount={usageCountsByCombo[key] || 0}
                      config={cfg}
                      expanded={expandedCombos.has(key)}
                      isDirty={dirtyCombos.has(key)}
                      onToggleExpanded={() => toggleExpanded(key)}
                      onUpdate={(patch) => updateConfig(key, patch)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <MeaganConfirmModal
        visible={showMeaganModal}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowMeaganModal(false)}
      />
    </div>
  );
};

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      'rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
      (active ? 'bg-blue-primary text-white' : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50')
    }
  >
    {label}
  </button>
);

interface TagsInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  dataTestId: string;
}

/**
 * Keep a draft string while typing so commas / spaces aren't immediately collapsed
 * back into the joined array (which blocked multi-tag entry and tags with spaces).
 */
const TagsInput: React.FC<TagsInputProps> = ({ tags, onChange, dataTestId }) => {
  const [draft, setDraft] = useState(() => tags.join(', '));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(tags.join(', '));
    }
  }, [tags, focused]);

  return (
    <input
      type="text"
      value={draft}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        onChange(parseTags(next));
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseTags(draft);
        setDraft(parsed.join(', '));
        onChange(parsed);
      }}
      placeholder="Virtual Care, Urgent"
      className="border-gray-light rounded-sm border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
      data-testid={dataTestId}
    />
  );
};

interface ComboCardProps {
  providerLabel: string;
  providerType: string;
  typeLabel: string;
  usageCount: number;
  config: ProviderTypeConfig;
  expanded: boolean;
  isDirty: boolean;
  onToggleExpanded: () => void;
  onUpdate: (patch: Partial<ProviderTypeConfig>) => void;
}

const ComboCard: React.FC<ComboCardProps> = ({
  providerLabel,
  providerType,
  typeLabel,
  usageCount,
  config,
  expanded,
  isDirty,
  onToggleExpanded,
  onUpdate,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onUpdate({ tile_image_file_data: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  const imagePreview = config.tile_image_file_data || config.tile_image_url;
  const isConfigured = config.id !== undefined;

  return (
    <div className={'rounded border bg-white shadow-sm ' + (usageCount === 0 ? 'opacity-80' : '')}>
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex w-full items-center justify-between border-b px-4 py-3 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{expanded ? '▼' : '▶'}</span>
          <h3 className="blue-primary text-base font-semibold">{typeLabel}</h3>
          {isDirty && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
              Unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isConfigured && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              Not configured
            </span>
          )}
          <span
            className={
              'rounded-full px-2 py-0.5 text-xs font-medium ' +
              (usageCount > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500')
            }
            title={`${providerLabel} ${typeLabel}`}
          >
            Used by {usageCount} {usageCount === 1 ? 'plan' : 'plans'}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-gray-dark block text-xs font-medium">Tile Name</label>
              <input
                type="text"
                value={config.tile_name || ''}
                onChange={(e) => onUpdate({ tile_name: e.target.value })}
                className="border-gray-light rounded-sm border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                data-testid={`${config.provider}-${providerType}-tile-name`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-gray-dark block text-xs font-medium">Tile Description</label>
              <input
                type="text"
                value={config.tile_description || ''}
                onChange={(e) => onUpdate({ tile_description: e.target.value })}
                className="border-gray-light rounded-sm border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                data-testid={`${config.provider}-${providerType}-tile-description`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-gray-dark block text-xs font-medium">Online Access URL</label>
              <input
                type="url"
                value={config.online_access_url || ''}
                onChange={(e) => onUpdate({ online_access_url: e.target.value })}
                className="border-gray-light rounded-sm border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                data-testid={`${config.provider}-${providerType}-online-access-url`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-gray-dark block text-xs font-medium">Tags (comma-separated)</label>
              <TagsInput
                tags={config.tile_tags || []}
                onChange={(tile_tags) => onUpdate({ tile_tags })}
                dataTestId={`${config.provider}-${providerType}-tile-tags`}
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            {imagePreview ? (
              <img src={imagePreview} alt="tile" className="h-16 w-16 rounded object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded border-2 border-dashed border-gray-300 text-xs text-gray-400">
                No image
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-sm border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                {imagePreview ? 'Replace image' : 'Upload image'}
              </button>
              <p className="mt-1 text-xs text-gray-400">JPEG, PNG, or WebP · max 5 MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImageChange}
              data-testid={`${config.provider}-${providerType}-tile-image-input`}
            />
          </div>

          <div>
            <label className="text-gray-dark mb-1 block text-xs font-medium">Member-facing modal content</label>
            <RichTextEditor
              content={config.content_html || ''}
              onChange={(value) => onUpdate({ content_html: value })}
              data-testid={`${config.provider}-${providerType}-content-editor`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlusCareContentPage;
